<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <div class="modal-header">
        <h3>{{ $t('credits.insufficientTitle') }}</h3>
        <button class="close-button" @click="close" :aria-label="$t('common.close')">
          &times;
        </button>
      </div>
      
      <div class="modal-body">
        <div class="credit-info">
          <div class="credit-icon">💰</div>
          <p class="main-message">
            {{ $t('credits.youNeed') }} <strong>{{ required }} {{ $t('credits.creditsToUse') }}</strong>
          </p>
          <div class="balance-info">
            <p>{{ $t('credits.yourBalance') }} <strong class="balance-amount">{{ balance }} {{ $t('credits.credits') }}</strong></p>
            <p class="insufficient-amount">{{ $t('credits.youNeedMore') }} <strong>{{ insufficient }} {{ $t('credits.moreCredits') }}</strong></p>
          </div>
        </div>
        
        <div class="service-info" v-if="serviceName">
          <p><strong>{{ $t('credits.service') }}</strong> {{ serviceName }}</p>
        </div>
        
        <div class="help-message">
          <p>{{ $t('credits.inviteToEarn') }}</p>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="inviteFriends" class="btn-primary">
          <span class="btn-icon">✉️</span>
          {{ $t('credits.inviteFriendsToEarn') }}
        </button>
        <button @click="close" class="btn-secondary">{{ $t('credits.gotIt') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  required: {
    type: Number,
    default: 3
  },
  balance: {
    type: Number,
    default: 0
  },
  service: {
    type: String,
    default: 'Humorous Resume Generation'
  }
});

const emit = defineEmits(['close', 'invite-friends']);

const serviceName = computed(() => props.service);
const insufficient = computed(() => Math.max(0, props.required - props.balance));

const close = () => {
  emit('close');
};

const inviteFriends = () => {
  emit('invite-friends');
  close(); // Close insufficient credits modal when opening invitation modal
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  color: #333;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  font-size: 2rem;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-button:hover {
  background: #f0f0f0;
  color: #333;
}

.modal-body {
  padding: 24px;
}

.credit-info {
  text-align: center;
  margin-bottom: 24px;
}

.credit-icon {
  font-size: 4rem;
  margin-bottom: 16px;
}

.main-message {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 16px;
  line-height: 1.6;
}

.main-message strong {
  color: var(--color-primary);
  font-weight: 600;
}

.balance-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
}

.balance-info p {
  margin: 8px 0;
  color: #666;
  font-size: 0.95rem;
}

.balance-amount {
  color: #4CAF50;
  font-size: 1.2rem;
  font-weight: 600;
}

.insufficient-amount {
  color: #ff9800;
  font-weight: 500;
}

.insufficient-amount strong {
  color: #ff9800;
}

.service-info {
  background: #f0f4ff;
  border-left: 4px solid var(--color-primary);
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.service-info p {
  margin: 0;
  color: #555;
  font-size: 0.9rem;
}

.help-message {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.help-message p {
  margin: 0;
  color: #666;
  font-size: 0.9rem;
  text-align: center;
}

.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: center;
}

.btn-primary:hover {
  background: #5568d3;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-icon {
  font-size: 1.1rem;
}

.btn-secondary {
  background: #f0f0f0;
  color: #333;
  border: 1px solid #ddd;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: #e0e0e0;
  border-color: #ccc;
}

.btn-secondary:active {
  transform: translateY(0);
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .modal-content {
    background: #1a1a1a;
    color: #e0e0e0;
  }

  .modal-header {
    border-bottom-color: #333;
  }

  .modal-header h3 {
    color: #e0e0e0;
  }

  .close-button {
    color: #999;
  }

  .close-button:hover {
    background: #333;
    color: #e0e0e0;
  }

  .balance-info {
    background: #2a2a2a;
  }

  .balance-info p {
    color: #ccc;
  }

  .service-info {
    background: #1a1f3a;
    border-left-color: var(--color-primary);
  }

  .service-info p {
    color: #ccc;
  }

  .help-message {
    border-top-color: #333;
  }

  .help-message p {
    color: #999;
  }

  .modal-footer {
    border-top-color: #333;
  }

  .btn-secondary {
    background: #2a2a2a;
    border-color: #444;
    color: #e0e0e0;
  }

  .btn-secondary:hover {
    background: #333;
    border-color: #555;
  }
}
</style>
